import { useState } from 'react';
import { TablePager, useSeiten } from '../components/TablePager';
import { FormError } from '../components/FormError';
import { RowActionGroup } from '../components/RowActions';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, Modal, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import {
  ApiError,
  masterDataDeleteControllerPriceList,
  usePriceListsControllerList,
  usePriceListsControllerCreate,
  useCustomersControllerList,
  getPriceListsControllerListQueryKey,
} from '@werkbank/shared';
import { formatDateDE } from '../lib/format';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';
import { EmptyState } from '../components/EmptyState';
import { DateInputDE } from '../components/DateInputDE';
import { DeleteButton } from '../components/MasterDataDialogs';
import { useHasFlag } from '../lib/permissions';
import { EditDialogFooter } from '../components/MasterDataDialogs';

export function PriceListsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [values, setValues] = useState({ customerId: '', name: '', validFrom: '' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Kundentest 2026-08: Löschen je Zeile — hartes Löschen ist die einzige
  // irreversible Stammdatenaktion (4.1b), daher eigener Flag deleteMasterData.
  const canDelete = useHasFlag('deleteMasterData');

  const lists = usePriceListsControllerList({});
  const customers = useCustomersControllerList({ archived: 'false', take: 1000 });

  const create = usePriceListsControllerCreate<ApiError>({
    mutation: {
      onSuccess: (pl) => {
        void queryClient.invalidateQueries({ queryKey: getPriceListsControllerListQueryKey() });
        setCreateOpen(false);
        void navigate({ to: '/price-lists/$priceListId', params: { priceListId: pl.id } });
      },
    },
  });

  const customerName = (id: string) => customers.data?.find((c) => c.id === id)?.displayName ?? id;

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(lists.data, 'preislisten');
  return (
    <>
      <ListPage
        title="Preislisten (EPA)"
        meta={lists.data ? 'Einheitspreisabkommen je Auftraggeber' : 'lädt…'}
        action={
          <Button onClick={() => setCreateOpen(true)} data-testid="pricelist-create">
            Neue Preisliste
          </Button>
        }
        state={listState(lists, lists.data?.length === 0)}
        errorText="Preislisten konnten nicht geladen werden."
        empty={
          <TableCard testId="pricelists-card">
            <EmptyState
              text="Noch keine Preislisten angelegt."
              action={<Button onClick={() => setCreateOpen(true)}>Neue Preisliste</Button>}
            />
          </TableCard>
        }
      >
        <TableCard
          testId="pricelists-card"
          footer={
            <TablePager
              p={seiten}
              einzahl="Preisliste"
              mehrzahl="Preislisten"
              testId="pricelists-pager"
            />
          }
        >
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Auftraggeber</Table.Th>
                <Table.Th>Gültig ab</Table.Th>
                <Table.Th>Gültig bis</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Positionen</Table.Th>
                {canDelete && <Table.Th aria-label="Aktionen" />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seiten.sichtbar.map((pl) => (
                <Table.Tr
                  key={pl.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    void navigate({
                      to: '/price-lists/$priceListId',
                      params: { priceListId: pl.id },
                    })
                  }
                >
                  <Table.Td>
                    <Text fw={500}>{pl.name}</Text>
                  </Table.Td>
                  <Table.Td>{customerName(pl.customerId)}</Table.Td>
                  <Table.Td className="wb-tnum">{formatDateDE(pl.validFrom)}</Table.Td>
                  <Table.Td className="wb-tnum">{formatDateDE(pl.validTo)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{pl.itemCount}</Table.Td>
                  {canDelete && (
                    // Kundentest 2026-08: eigene Positionen (Kinder) löscht der
                    // Server mit; blockierend sind nur Außenreferenzen (Beleg-
                    // positionen) — dann zeigt der DeleteButton die feldgenaue
                    // Serverantwort (4.1b). stopPropagation, damit der Zeilenklick
                    // nicht zusätzlich in die Detailansicht navigiert.
                    <Table.Td onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                      <RowActionGroup>
                        <DeleteButton
                          asIcon
                          label="Löschen"
                          title={`Preisliste „${pl.name}" löschen`}
                          question={`Soll die Preisliste „${pl.name}" gelöscht werden?`}
                          detail="Ihre Positionen fallen mit. Blockierend sind nur Außenreferenzen — dann nennt die Meldung sie."
                          onDelete={() =>
                            masterDataDeleteControllerPriceList(pl.id).then(() =>
                              queryClient.invalidateQueries({
                                queryKey: getPriceListsControllerListQueryKey(),
                              }),
                            )
                          }
                          testId={`price-list-delete-${pl.id}`}
                        />
                      </RowActionGroup>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableCard>
      </ListPage>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Neue Preisliste">
        <Stack gap="md">
          <Select
            label="Auftraggeber"
            required
            searchable
            data={customers.data?.map((c) => ({ value: c.id, label: c.displayName })) ?? []}
            value={values.customerId || null}
            onChange={(v) => setValues({ ...values, customerId: v ?? '' })}
            data-testid="pricelist-customer"
          />
          <TextInput
            label="Name"
            required
            placeholder="z. B. EPA GWH 2027"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.currentTarget.value })}
            data-testid="pricelist-name"
          />
          <DateInputDE
            label="Gültig ab"
            required
            value={values.validFrom}
            onChange={(iso) => setValues({ ...values, validFrom: iso })}
            data-testid="pricelist-validfrom"
          />
          <FormError error={create.error} />
          <EditDialogFooter
            error={null}
            onCancel={() => setCreateOpen(false)}
            onSave={() =>
              create.mutate({
                data: {
                  customerId: values.customerId,
                  name: values.name.trim(),
                  validFrom: values.validFrom.trim(),
                },
              })
            }
            saving={create.isPending}
            saveTestId="pricelist-save"
            saveLabel="Anlegen"
          />
        </Stack>
      </Modal>
    </>
  );
}
